// 导入axios
import axios from 'axios'
import router from '../router'

// 创建基础axios实例
const createBaseAPI = (config = {}) => {
  const api = axios.create({
    baseURL: '', // 不使用baseURL，所有请求路径都明确包含/api前缀
    timeout: 10000, // 请求超时时间
    headers: {
      'Content-Type': 'application/json'
    },
    ...config
  })

  // 请求拦截器
  api.interceptors.request.use(
    config => {
      // 在发送请求之前做些什么，比如添加token
      const token = localStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `${token}`
      }
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // 响应拦截器 - 重构为单一函数处理所有响应
  api.interceptors.response.use(
    response => {
      // 特殊处理blob和arraybuffer类型响应，直接返回完整响应
      if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
        return response;
      }
      
      // 统一处理接口返回，响应格式为 {code, msg, data}
      const result = response.data
      
      // 首先检查HTTP状态码
      if (response.status >= 200 && response.status < 300) {
        // HTTP状态码成功
        if (result && typeof result.code === 'number') {
          if (result.code === 0) {
            // 业务逻辑成功，返回data数据
            return result.data
          } else if (result.code === 401) {
            // 业务逻辑返回401（未授权），也需要处理
            // 为了复用handleHttpError逻辑，创建一个模拟的401响应对象
            const mock401Response = {
              status: 401,
              config: response.config,
              data: result
            }
            handleHttpError(mock401Response)
            return Promise.reject(new Error(result.msg || '未授权'))
          } else {
            // 其他业务逻辑错误
            return Promise.reject(new Error(result.msg || '业务逻辑错误'))
          }
        }
        // 兼容不符合标准格式的响应
        return result
      } else {
        // HTTP状态码错误，统一处理
        handleHttpError(response)
        return Promise.reject(new Error('HTTP请求错误: ' + response.status))
      }
    },
    error => {
      // 处理网络错误或请求被取消等情况
      if (error.response) {
        // 服务器返回了错误状态码
        handleHttpError(error.response)
      } else if (error.request) {
        // 请求已发送但没有收到响应
        console.error('网络错误，无法连接到服务器')
      } else {
        // 请求配置出错
        console.error('请求配置错误:', error.message)
      }
      return Promise.reject(error)
    }
  )

  // 处理HTTP错误的函数
  function handleHttpError(response) {
    const status = response.status
    const url = response.config ? response.config.url : ''
    
    switch (status) {
      case 401:
        // 未授权，跳转到登录页，但排除登录和退出接口
        // 考虑到请求会添加/api前缀，使用更精确的匹配方式
        const isLoginRequest = url.endsWith('/login') || url.endsWith('/api/login')
        const isLogoutRequest = url.endsWith('/logout') || url.endsWith('/api/logout')
        
        if (!isLoginRequest && !isLogoutRequest) {
          // 清除所有认证信息
          localStorage.removeItem('isLoggedIn')
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          
          // 使用Vue Router进行跳转，与路由系统保持一致
          // 使用replace避免用户返回到需要授权的页面
          router.replace('/')
        }
        break
      case 403:
        console.error('权限不足')
        break
      case 404:
        console.error('请求的资源不存在')
        break
      case 500:
        // 500错误时，优先使用响应中的msg作为提示信息
        const errorData = response.data
        if (errorData && errorData.msg) {
          console.error(errorData.msg)
        } else {
          console.error('服务器错误')
        }
        break
      default:
        console.error('请求失败')
    }
  }

  return api
}

// 创建默认API实例
const api = createBaseAPI()

// 添加统一的请求拦截器，确保所有请求都包含/api前缀
const apiWithPrefix = {
  get: (url, config) => api.get(`/api${url.startsWith('/') ? '' : '/'}${url}`, config),
  post: (url, data, config) => api.post(`/api${url.startsWith('/') ? '' : '/'}${url}`, data, config),
  put: (url, data, config) => api.put(`/api${url.startsWith('/') ? '' : '/'}${url}`, data, config),
  delete: (url, config) => api.delete(`/api${url.startsWith('/') ? '' : '/'}${url}`, config),
  patch: (url, data, config) => api.patch(`/api${url.startsWith('/') ? '' : '/'}${url}`, data, config)
}

// 导出带有/api前缀的API实例
export { createBaseAPI }
export default apiWithPrefix